import { useCallback, useEffect, useMemo } from 'react';
import useSwr from 'swr';
import { useHistory } from 'react-router-dom';

import PollCardList from '@components/elements/PollCardList';

import { formatPollData, FormattedPoll } from '@utils/Poll';

import { getPollDetail } from '@sdk/managers/PollsManager';
import { getVotedPollInfo } from '@sdk/managers/VotesManager';

import { useAuth } from '@hooks/Auth';
import toast from 'react-hot-toast';

interface getRecentPollsResponse {
  items: FormattedPoll[];
  nextToken: number;
}

const getRecentPolls = async (
  _query: string,
  userId: string
): Promise<getRecentPollsResponse> => {
  if (!userId) throw new Error('You are not logged in.');
  const votedRes: any = await getVotedPollInfo(
    [
      {
        items: ['subPollID', 'pollID', { option: ['id'] }],
      },
      'nextToken',
    ],
    userId,
    undefined
  );

  const votedMainPolls = votedRes.data.result.items.filter(
    (item) => item.subPollID === null
  );

  const votedPolls: FormattedPoll[] = await Promise.all(
    votedMainPolls.map(async (item) => {
      const res = await getPollDetail(
        [
          'id',
          'title',
          'status',
          'owner',
          'startTime',
          'endTime',
          'image { key bucket region }',
          {
            options: ['id', 'description'],
          },
          { voteResults: [{ items: ['count'] }] },
        ],
        item.pollID
      );
      if (!res.data.result) return null;

      return formatPollData(res.data.result);
    })
  );

  return {
    nextToken: votedRes.data.result.nextToken,
    items: votedPolls.filter((poll) => {
      if (poll === null) return false;
      if (poll.status === 'CLOSED') return false;
      return true;
    }),
  };
};

const FeedPage: React.FC = () => {
  const history = useHistory<any>();
  const { dbUser } = useAuth();

  const { data, error } = useSwr<getRecentPollsResponse>(
    dbUser?.id ? ['/poll/voted', dbUser?.id] : null,
    getRecentPolls
  );

  useEffect(() => {
    if (error?.message) toast.error(error.message);
  }, [error]);

  const onPollSelect = useCallback(
    (poll: FormattedPoll) => {
      history.push(`/poll/${poll.id}`);
    },
    [history]
  );

  const [finishedPolls, recentPolls] = useMemo(() => {
    if (!data) return [undefined, undefined];
    const finished: FormattedPoll[] = [];
    const recent: FormattedPoll[] = [];

    const now = new Date();

    data.items
 
      .forEach((poll) => {
        if (poll.endDate < now) finished.push(poll);
        else recent.push(poll);
      });

    return [finished, recent];
  }, [data]);

  return (
    <div>
      <h1 className="mt-8 mb-2 text-2xl font-bold text-white">Your Votes</h1>

      {(finishedPolls?.length || 0) > 0 && (
        <>
          <h2 className="mt-8 mb-2 text-lg font-bold text-green">Finished</h2>
          <PollCardList
            data={finishedPolls}
            loading={!data}
            onSelect={onPollSelect}
            accessory="View Result"
          />
        </>
      )}

      {(recentPolls?.length || 0) > 0 && (
        <>
          <h2 className="mt-8 mb-2 text-lg font-bold text-cyan">
            Your Recent Votes
          </h2>
          <PollCardList
            data={recentPolls}
            loading={!data}
            onSelect={onPollSelect}
            type="border"
            accessory="Manage"
          />
        </>
      )}
    </div>
  );
};

export default FeedPage;
